import { type ReactElement, type ReactNode, useCallback, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useLocation } from 'react-router';
import { usePrevious } from 'react-use';
import type { Scope } from '@sentry/react';

import { ErrorView } from './errors/ErrorView';
import type { ErrorVariant } from './errors/errorLayouts';

interface GErrorBoundaryProps {
  children: ReactNode;
  resetOnNavigation?: boolean;
  onError?: (options: { error: unknown; scope: Scope }) => void;
  variant?: ErrorVariant;
  className?: string;
  extra?: ReactElement;
}

const GErrorFallback = ({
  error,
  resetError,
  resetOnNavigation,
  variant,
  className,
  children,
}: {
  error: unknown;
  resetError: () => void;
  resetOnNavigation: boolean;
  variant: ErrorVariant;
  className?: string;
  children?: ReactElement;
}): ReactElement => {
  const location = useLocation();
  const prevLocationPathname = usePrevious(location.pathname);

  useEffect(() => {
    if (resetOnNavigation && prevLocationPathname && prevLocationPathname !== location.pathname) {
      resetError();
    }
  }, [resetError, resetOnNavigation, location.pathname, prevLocationPathname]);

  return (
    <ErrorView cause={error} variant={variant} className={className}>
      {children}
    </ErrorView>
  );
};

const GErrorBoundary = ({
  children,
  resetOnNavigation = false,
  onError,
  variant = 'inline',
  className,
  extra,
}: GErrorBoundaryProps): ReactElement => {
  const FallbackComponent = useCallback(
    ({ error, resetError }: { error: unknown; resetError: () => void }) => (
      <GErrorFallback
        error={error}
        resetError={resetError}
        resetOnNavigation={resetOnNavigation}
        variant={variant}
        className={className}
      >
        {extra}
      </GErrorFallback>
    ),
    [resetOnNavigation, variant, className, extra]
  );

  return (
    <Sentry.ErrorBoundary
      fallback={FallbackComponent}
      beforeCapture={(scope: Scope, error: unknown) => {
        if (onError) {
          onError({ error, scope });
        }
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default GErrorBoundary;
